import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useLang, tx } from "@/context/LangContext";
import { INSTAGRAM, T } from "@/data/content";
import { FadeUp, SectionLabel, BlurBackdrop } from "@/components/Reveal";
import { ArrowUpRight, BadgeCheck, Instagram as InstagramIcon, Link2 } from "lucide-react";

const CountUp = ({ value }) => {
    const { lang } = useLang();
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-10% 0px" });
    const [n, setN] = useState(0);

    useEffect(() => {
        if (!inView) return;
        let raf;
        const t0 = performance.now();
        const tick = (now) => {
            const p = Math.min(1, (now - t0) / 1600);
            setN(Math.round(value * (1 - Math.pow(1 - p, 4))));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [inView, value]);

    return (
        <span ref={ref}>
            {n.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}
        </span>
    );
};

export default function Instagram() {
    const { lang } = useLang();
    const max = Math.max(...INSTAGRAM.stats.map((s) => s.value));

    return (
        <section
            id="instagram"
            data-testid="instagram-section"
            className="relative overflow-hidden bg-ink py-24 text-cream md:py-36"
        >
            <BlurBackdrop src="/img/kallykurls-01.jpg" tint="bg-ink/70" />
            <div className="mx-auto max-w-[1600px] px-5 md:px-10">
                <SectionLabel
                    en={tx(lang, T.insta.label)}
                    fr={tx(lang, T.insta.label)}
                />

                <div className="mt-8 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                    <FadeUp delay={0.1}>
                        <h2 className="font-display text-[clamp(2.6rem,8vw,7rem)] font-extrabold uppercase leading-none tracking-[-0.02em]">
                            {tx(lang, T.insta.title)}
                            <span className="text-violet">.</span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.2}>
                        <p className="max-w-xs font-mono text-[9px] leading-relaxed tracking-[0.3em] text-cream/40 md:text-[10px]">
                            {tx(lang, T.insta.sub)}
                        </p>
                    </FadeUp>
                </div>

                {/* monthly average — computed from the 30-day stat */}
                <FadeUp delay={0.05} className="mt-10 md:mt-12">
                    <div
                        data-testid="instagram-monthly"
                        className="flex flex-col gap-3 border-y border-white/10 py-6 md:flex-row md:items-end md:justify-between"
                    >
                        <p className="font-mono text-[9px] tracking-[0.3em] text-cream/40 md:text-[10px]">
                            {tx(lang, T.insta.monthly)}
                        </p>
                        <div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-4">
                            <p className="font-display text-5xl font-extrabold leading-none tracking-tight md:text-7xl">
                                <CountUp value={INSTAGRAM.monthlyAvg} />
                            </p>
                            <p className="pb-1 font-mono text-[9px] tracking-[0.25em] text-cream/35 md:text-[10px]">
                                {tx(lang, T.insta.monthlyNote)}
                            </p>
                        </div>
                    </div>
                </FadeUp>

                <div className="mt-12 grid grid-cols-1 gap-6 md:mt-16 md:grid-cols-12 md:gap-8">
                    {/* written account card — real info, no screenshot */}
                    <FadeUp delay={0.1} className="md:col-span-5">
                        <div
                            data-testid="instagram-profile-card"
                            className="relative overflow-hidden rounded-[28px] border border-white/15 bg-[#101014] p-7 text-cream md:p-9"
                        >
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-violet/25 blur-3xl"
                            />
                            <div
                                aria-hidden="true"
                                className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full bg-blood/20 blur-3xl"
                            />
                            <div className="relative">
                                <div className="flex items-center gap-5">
                                    <span className="shrink-0 rounded-full bg-gradient-to-tr from-gold via-blood to-violet p-[3px]">
                                        <span className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-[#101014] bg-ink font-display text-xl font-extrabold tracking-tight text-cream">
                                            C.
                                        </span>
                                    </span>
                                    <div>
                                        <p className="flex items-center gap-2 font-display text-xl font-extrabold tracking-tight">
                                            casper.xx6
                                            <BadgeCheck size={16} className="text-violet" />
                                        </p>
                                        <p className="mt-1 font-mono text-[9px] tracking-[0.25em] text-cream/50">
                                            {tx(lang, T.insta.role)}
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-7 grid grid-cols-3 divide-x divide-white/10 border-y border-white/10 py-5 text-center">
                                    {[
                                        { v: INSTAGRAM.posts, l: T.insta.posts },
                                        { v: INSTAGRAM.followers, l: T.insta.followers },
                                        { v: INSTAGRAM.following, l: T.insta.following },
                                    ].map((s) => (
                                        <div key={s.l.en}>
                                            <p className="font-display text-2xl font-extrabold tracking-tight md:text-3xl">
                                                {s.v.toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}
                                            </p>
                                            <p className="mt-1 font-mono text-[8px] tracking-[0.22em] text-cream/45 md:text-[9px]">
                                                {tx(lang, s.l)}
                                            </p>
                                        </div>
                                    ))}
                                </div>

                                <p className="mt-6 font-mono text-[9px] tracking-[0.3em] text-cream/45">
                                    {tx(lang, T.insta.realisation)}
                                </p>
                                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                                    {INSTAGRAM.accounts.map((a) => (
                                        <a
                                            key={a.handle}
                                            href={a.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid={`instagram-bio-${a.handle.slice(1).replace(/\./g, "-")}`}
                                            className="font-mono text-[10px] tracking-[0.15em] text-violet transition-colors hover:text-cream"
                                        >
                                            {a.handle}
                                        </a>
                                    ))}
                                </div>
                                <a
                                    href={INSTAGRAM.linktree}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="instagram-linktree"
                                    className="mt-4 inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-cream/60 transition-colors hover:text-cream"
                                >
                                    <Link2 size={12} />
                                    linktr.ee/Casper.xx6
                                </a>

                                <a
                                    href={INSTAGRAM.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="instagram-follow-button"
                                    data-cursor="VIEW"
                                    className="group/btn mt-7 flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-violet via-blood to-gold px-6 py-4 font-mono text-[10px] tracking-[0.3em] text-cream transition-transform duration-500 hover:scale-[1.02]"
                                >
                                    <InstagramIcon size={14} />
                                    {tx(lang, T.insta.follow)}
                                    <ArrowUpRight
                                        size={14}
                                        className="transition-transform duration-500 group-hover/btn:rotate-45"
                                    />
                                </a>
                            </div>
                        </div>
                    </FadeUp>

                    {/* animated stats */}
                    <div className="flex flex-col justify-center gap-9 md:col-span-7 md:pl-10">
                        {INSTAGRAM.stats.map((s, i) => (
                            <FadeUp key={s.days} delay={0.1 + i * 0.1}>
                                <div data-testid={`instagram-stat-${s.days}`}>
                                    <div className="flex items-end justify-between gap-4">
                                        <p className="font-display text-5xl font-extrabold tracking-tight md:text-7xl">
                                            <CountUp value={s.value} />
                                        </p>
                                        <p className="whitespace-nowrap font-mono text-[9px] tracking-[0.25em] text-cream/40 md:text-[10px]">
                                            {lang === "fr"
                                                ? `VUES — ${s.days} DERNIERS JOURS`
                                                : `VIEWS — LAST ${s.days} DAYS`}
                                        </p>
                                    </div>
                                    <div className="mt-3 h-[3px] w-full bg-white/10">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            whileInView={{
                                                width: `${(s.value / max) * 100}%`,
                                            }}
                                            viewport={{ once: true }}
                                            transition={{
                                                duration: 1.4,
                                                delay: 0.2 + i * 0.12,
                                                ease: [0.16, 1, 0.3, 1],
                                            }}
                                            className="h-full bg-gradient-to-r from-violet via-blood to-gold"
                                        />
                                    </div>
                                </div>
                            </FadeUp>
                        ))}
                        <FadeUp delay={0.4}>
                            <p className="font-mono text-[9px] tracking-[0.3em] text-cream/35">
                                {tx(lang, T.insta.note)}
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </div>
        </section>
    );
}
